<template>
  <div class="campo-select-container mb-3" :class="containerClass">
    <label
      v-if="(label || $slots.label) && !hideLabel"
      :for="id"
      class="form-label"
      :class="{ 'is-required': isObrigatorio }"
    >
      <slot name="label">{{ label }}</slot>
      <i
        v-if="labelInfo"
        class="bi bi-info-circle text-primary ms-1"
        :title="labelInfo"
        style="cursor: help; font-size: 0.85em;"
      ></i>
    </label>

    <div class="position-relative has-validation">
      <select
        :id="id"
        class="form-select"
        :class="{ 'is-invalid': !!errorMessage, 'placeholder-selected': isPlaceholderSelected }"
        v-model="valorInterno"
        :disabled="disabled"
        v-bind="$attrs"
        @keydown="limparSelecao"
      >
        <option value="" class="placeholder-option">{{ placeholder }}</option>
        <slot />
        <option
          v-for="option in optionsFiltradas"
          :key="getOptionValue(option)"
          :value="getOptionValue(option)"
        >
          {{ getOptionLabel(option) }}
        </option>
      </select>

      <div class="invalid-feedback" v-if="errorMessage">
        {{ errorMessage }}
      </div>
    </div>

    <div v-if="dica && !errorMessage" class="form-text text-body-secondary small">
      {{ dica }}
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue'

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, default: '' },
  modelValue: { type: [String, Number, Boolean], default: '' },
  options: { type: Array, default: () => [] },
  optionValue: { type: String, default: 'id' },
  optionLabel: { type: String, default: 'nome' },
  error: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  required: { type: [Boolean, String, Number], default: false },
  placeholder: { type: String, default: 'Selecione...' },
  hideLabel: { type: Boolean, default: false },
  containerClass: { type: String, default: '' },
  dica: { type: String, default: '' },
  labelInfo: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])

const formErrors = inject('formErrors', ref({}))
const errorMessage = computed(() => props.error || formErrors.value[props.id] || '')

const valorInterno = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

function limparSelecao (event) {
  if (event.key === 'Delete' || event.key === 'Backspace') {
    emit('update:modelValue', '')
  }
}

const isPlaceholderSelected = computed(() => {
  const v = props.modelValue
  return v === '' || v === null || v === undefined
})

const isObrigatorio = computed(() => {
  return props.required === true || props.required === 'true' || props.required === 'required' || props.required === ''
})

const getOptionValue = (option) => {
  if (typeof option === 'object' && option !== null) return option[props.optionValue]
  return option
}

const getOptionLabel = (option) => {
  if (typeof option === 'object' && option !== null) return option[props.optionLabel]
  return option
}

// GUARDIÃO: Filtra itens inativos do dropdown, mas sempre inclui o valor atualmente selecionado
// para não quebrar a exibição de valores legados armazenados em outras tabelas.
const optionsFiltradas = computed(() => {
  if (!Array.isArray(props.options)) return props.options

  const selectedValue = props.modelValue
  return props.options.filter(opt => {
    // Se o item não tem campo Ativo, não é item de lista -> passa livre
    if (typeof opt !== 'object' || opt === null) return true
    if (opt.Ativo === undefined) return true
    // Se está ativo, mostra
    if (opt.Ativo === true) return true
    // Se está inativo mas é o valor atualmente selecionado, mantém para exibição
    if (selectedValue && String(getOptionValue(opt)) === String(selectedValue)) return true
    // Inativo e não selecionado -> oculta
    return false
  })
})
</script>

<style scoped>
.form-label { font-weight: 400 !important; }
.form-label.is-required::after { content: " *"; color: var(--bs-danger); font-weight: bold; }
.campo-select-container { display: flex; flex-direction: column; }
.form-select option { color: var(--bs-body-color); }
.form-select option.placeholder-option { color: var(--bs-secondary-color); }
.form-select.placeholder-selected { color: var(--bs-secondary-color); }
</style>
